import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  Lightning,
} from "@phosphor-icons/react";
import {
  fetchTrending,
  CATEGORIES,
  newsletterSubscribe,
  formatApiError,
} from "@/lib/api";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { useScrollObserver } from "@/hooks/useScrollObserver";
import InfiniteNewsFeed from "@/components/InfiniteNewsFeed";
import NewsCard from "@/components/NewsCard";
import SkeletonCard from "@/components/SkeletonCard";
import SEO from "@/components/SEO";

const CATEGORY_ICONS = {
  world:         "🌍",
  technology:    "💡",
  business:      "📈",
  sports:        "⚽",
  entertainment: "🎬",
  science:       "🔬",
  health:        "🩺",
};

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function BottomEmailForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) { setErr("Please enter a valid email."); return; }
    setErr("");
    setBusy(true);
    try {
      await newsletterSubscribe(email, "homepage_bottom");
      setDone(true);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-white/20 border border-white/30 rounded-lg">
        <CheckCircle size={16} className="text-white flex-shrink-0" weight="fill" />
        <span className="text-sm text-white font-medium">Check your inbox to confirm!</span>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-md">
      <div className="flex-1">
        <input
          type="email"
          required
          value={email}
          onChange={(ev) => { setEmail(ev.target.value); setErr(""); }}
          placeholder="your@email.com"
          className="w-full bg-white/10 border border-white/30 text-white placeholder-white/60 px-4 py-3 text-sm rounded-lg outline-none focus:bg-white/20 focus:border-white/50 focus:ring-2 focus:ring-white/20 transition-colors"
          aria-label="Email for marketing digest"
        />
        {err && <p className="mt-1 text-xs text-white/80" role="alert">{err}</p>}
      </div>
      <button
        type="submit"
        disabled={busy}
        className="bg-white text-blue-700 font-semibold px-5 py-3 text-sm rounded-lg hover:bg-blue-50 disabled:opacity-60 transition-colors whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-white/50"
      >
        {busy ? "Subscribing…" : "Subscribe free"}
      </button>
    </form>
  );
}

export default function HomePage() {
  const fetchFn     = useCallback((page) => fetchTrending("top", "US", page, 12), []);
  const getCacheKey = useCallback((page) => `news_top_US_home_${page}`, []);
  const feed        = useInfiniteFeed({ fetchFn, feedKey: "top_US_home", getCacheKey, ttlMs: 15 * 60 * 1000 });
  const sentinelRef = useScrollObserver(feed.loadMore);

  const [aiNews, setAiNews] = useState([]);
  useEffect(() => {
    fetchTrending("technology", "US", 1, 4)
      .then((items) => { if (Array.isArray(items)) setAiNews(items.slice(0, 4)); })
      .catch(() => {});
  }, []);

  const categoryList = CATEGORIES.filter((c) => c.slug !== "top");
  const [categoryFeeds, setCategoryFeeds] = useState({});
  const [categoryLoading, setCategoryLoading] = useState(true);
  useEffect(() => {
    Promise.allSettled(
      categoryList.map((c) => fetchTrending(c.slug, "US", 1, 6))
    ).then((results) => {
      const feeds = {};
      results.forEach((r, i) => {
        if (r.status === "fulfilled" && Array.isArray(r.value)) {
          feeds[categoryList[i].slug] = r.value.slice(0, 6);
        }
      });
      setCategoryFeeds(feeds);
      setCategoryLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const featuredHero  = feed.items[0] ?? null;
  const featuredStrip = feed.items.slice(3, 7);

  return (
    <>
      <SEO
        title="TrendMing — Free AI-Powered News, Filtered & Summarized"
        description="TrendMing uses AI to filter out the noise and summarize today's top news — world, business, tech, and health — sorted by category. Free, no paywall."
        canonical="https://trendming.com/"
      />

      {/* ══════════════════════ SECTION 2 · FEATURED NEWS ══ */}
      <section className="py-8 md:py-12 border-b border-slate-200" data-testid="featured-news-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">/ Featured Stories</span>
              <h2 className="font-bold text-3xl md:text-4xl text-slate-900 mt-2">Top stories right now</h2>
            </div>
            <Link
              to="/category/top"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-blue-600 transition-colors"
              data-testid="see-all-featured-link"
            >
              See all stories <ArrowRight size={14} />
            </Link>
          </div>

          {feed.loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-slate-100 rounded-xl animate-pulse h-[280px]" aria-hidden="true" />
              ))}
            </div>
          ) : featuredHero ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
                <div className="lg:col-span-8">
                  <NewsCard item={featuredHero} variant="hero" index={0} />
                </div>
                <div className="lg:col-span-4">
                  <div className="border border-slate-200 rounded-2xl overflow-hidden h-full flex flex-col">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Top Headlines</span>
                      <span className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-600 uppercase tracking-wider">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 live-dot" aria-hidden="true" />
                        Live
                      </span>
                    </div>
                    <div className="divide-y divide-slate-100 flex-1">
                      {feed.items.slice(1, 7).map((item, i) => (
                        <a
                          key={item.id}
                          href={item.slug ? `/news/${item.slug}` : `/article/${item.id}`}
                          className="flex gap-3 px-4 py-3 hover:bg-slate-50 group transition-colors"
                        >
                          <span className="text-[11px] font-bold text-blue-500 shrink-0 w-4 mt-0.5">{i + 1}</span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800 group-hover:text-blue-700 line-clamp-2 leading-snug transition-colors">
                              {item.title}
                            </p>
                          </div>
                        </a>
                      ))}
                    </div>
                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                      <Link
                        to="/category/top"
                        className="w-full inline-flex items-center justify-center gap-1.5 border border-blue-200 hover:bg-blue-50 text-blue-700 font-semibold py-2 text-xs rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      >
                        See all stories <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              {featuredStrip.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {featuredStrip.map((item, i) => (
                    <NewsCard key={item.id} item={item} index={i + 3} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-slate-400 text-sm py-10 text-center">No stories yet — check back soon.</p>
          )}
        </div>
      </section>

      {/* ══════════════════════ SECTION 3 · AI NEWS TEASER ══ */}
      {aiNews.length > 0 && (
        <section className="py-8 md:py-10 border-b border-slate-200 bg-slate-50" data-testid="ai-news-teaser">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Lightning size={13} className="text-blue-600" weight="fill" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">/ AI This Week</span>
                </div>
                <h2 className="font-bold text-2xl md:text-3xl text-slate-900">What's moving in AI right now</h2>
                <p className="text-sm text-slate-500 mt-1">The biggest AI stories from our live intelligence dashboard.</p>
              </div>
              <Link
                to="/trending"
                className="inline-flex items-center gap-1.5 border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-400 px-4 py-2 rounded-full text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400/30"
              >
                Open the AI Hub <ArrowRight size={11} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aiNews.map((item, i) => (
                <NewsCard key={item.id} item={item} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════ SECTION 5 · INFINITE FEED ══ */}
      <section id="trending" className="py-8 md:py-12 border-b border-slate-200" data-testid="trending-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">/ Trending Now</span>
              <h2 className="font-bold text-3xl md:text-4xl text-slate-900 mt-2">Top stories worldwide</h2>
            </div>
            <Link
              to="/category/world"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-blue-600 transition-colors"
              data-testid="see-all-world-link"
            >
              See all world news <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 items-start">
            <div>
              <InfiniteNewsFeed feed={feed} sentinelRef={sentinelRef} heroFirst testId="homepage-feed-grid" />
            </div>
            <aside className="sticky top-24 space-y-5 hidden lg:block">
              {/* Browse Topics */}
              <div className="border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Browse Topics</span>
                </div>
                <div className="p-4 flex flex-wrap gap-2">
                  {CATEGORIES.filter((c) => c.slug !== "top").map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* AI Hub card */}
              <div className="border border-blue-200 bg-blue-50 rounded-2xl p-4">
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">/ AI Intelligence</span>
                <h3 className="font-bold text-base text-slate-900 mt-1 mb-2">What's moving in AI right now</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                  Model drops, funding rounds, tool launches — tracked in real time.
                </p>
                <Link
                  to="/trending"
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 text-xs rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                >
                  Open AI Hub <ArrowRight size={11} />
                </Link>
              </div>

            </aside>
          </div>
        </div>
      </section>

      {/* ══════════════════════ SECTION 6 · ALL CATEGORIES ══ */}
      <section className="py-4 md:py-6" data-testid="all-categories-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 space-y-14">
          {categoryList.map((cat) => {
            const items = categoryFeeds[cat.slug] || [];
            const isLoading = categoryLoading && items.length === 0;
            return (
              <div key={cat.slug} data-testid={`category-section-${cat.slug}`}>
                {/* Category heading */}
                <div className="flex items-end justify-between mb-5 flex-wrap gap-4 border-b border-slate-200 pb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl" aria-hidden="true">{CATEGORY_ICONS[cat.slug]}</span>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                        / {cat.label}
                      </span>
                      <h2 className="font-bold text-2xl md:text-3xl text-slate-900 mt-0.5">
                        {cat.label}
                      </h2>
                    </div>
                  </div>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-blue-600 transition-colors"
                  >
                    See all {cat.label} <ArrowRight size={14} />
                  </Link>
                </div>

                {/* Cards grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {isLoading
                    ? [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
                    : items.map((item, i) => (
                        <NewsCard key={item.id} item={item} index={i} />
                      ))}
                </div>

                {/* Footer link */}
                {!isLoading && items.length > 0 && (
                  <div className="mt-5 text-center">
                    <Link
                      to={`/category/${cat.slug}`}
                      className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 hover:border-blue-300 text-slate-600 hover:text-blue-600 px-6 py-2.5 rounded-full text-sm font-semibold transition-colors"
                    >
                      See all {cat.label} stories <ArrowRight size={13} />
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ══════════════════════ SECTION 7 · NEWSLETTER CTA ══ */}
      <section id="newsletter" className="bg-blue-600 py-8 md:py-12" data-testid="newsletter-cta-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-200">
                / Marketing Intelligence · Weekly
              </span>
              <h2 className="font-bold text-3xl md:text-4xl text-white mt-2">
                Never miss a story that moves the needle.
              </h2>
              <p className="mt-4 text-blue-100 text-base leading-relaxed max-w-lg">
                Curated marketing news, tool launches, and one actionable tactic —
                delivered every Friday. Free, unsubscribe any time.
              </p>
              <ul className="mt-4 space-y-2" aria-label="Digest benefits">
                {[
                  "5-minute Friday read, zero fluff",
                  "New tool spotlights every week",
                  "Marketing tactics that actually work",
                  "Unsubscribe any time, no questions asked",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-blue-100">
                    <CheckCircle size={14} className="text-blue-300 flex-shrink-0" weight="fill" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <BottomEmailForm />
              <p className="mt-3 text-xs text-blue-300">No spam. Unsubscribe any time.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
